import { Form, Input, message } from 'antd';
import { SortedSelect } from 'components/ui/sorted-select';
import {
  fields,
  getValidationSchema,
  initialValues,
} from 'components/features/reinsured-form/form';
import { AddressForm } from 'components/ui/address-form';
import { ConfirmationModal } from 'components/ui/confirmation-modal';
import { ConfirmationModalType } from 'components/ui/confirmation-modal/types';
import FormActionButtons from 'components/ui/form-action-buttons';
import FormContainer from 'components/ui/form-container';
import FormLayoutGroup from 'components/ui/form-layout-group';
import FormSection from 'components/ui/form-section';
import StatusIndicator from 'components/ui/status-indicator';
import { ENavigationPath } from 'constants/path';
import { debounce, isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router';
import { handleError, sanitizeFormValue, validateForm } from 'utils/form';
import { AddressFormInitialData } from 'components/ui/address-form/types';
import { reinsuredApi } from 'services/apis';
import {
  CreateReinsuredPayloadDto,
  CreateReinsuredPayloadDtoReinsuredGroupEnum,
  CreateReinsuredPayloadDtoStatusEnum,
} from 'libs/pinnacle-sdk';
const { TextArea } = Input;

interface ReinsuredFormProps {
  mode?: 'create' | 'update';
  initialData?: object;
  onSubmit?: (data: CreateReinsuredPayloadDto) => Promise<void>;
  isLoading?: boolean;
}

const ReinsuredForm = ({
  mode = 'create',
  initialData = initialValues,
  onSubmit,
  isLoading = false,
}: ReinsuredFormProps) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const createReinsuredMutation = useMutation(
    (reinsured: CreateReinsuredPayloadDto) =>
      reinsuredApi.createReinsured({
        createReinsuredPayloadDto: reinsured,
      }),
    {
      onSuccess: () => {
        message.success(
          t('forms.createRecordSuccess', { type: t('reinsured.reinsured') })
        );
        resetAndNavigate();
      },
      onError: async (error: { response: Response }) =>
        handleError({
          error,
          defaultMessage: t('forms.createRecordFail', {
            type: t('reinsured.reinsured'),
          }),
        }),
    }
  );
  const [form] = Form.useForm<CreateReinsuredPayloadDto>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue(initialData);
      // Validate form after setting initial values
      validateForm<CreateReinsuredPayloadDto>(
        initialData as CreateReinsuredPayloadDto,
        form,
        fields,
        getValidationSchema(t)
      ).then(setIsFormValid);
    }
  }, [form, initialData, t]);

  const resetAndNavigate = useCallback(() => {
    form.resetFields();
    setShowConfirmation(false);
    return navigate(ENavigationPath.REINSURED_RECORDS);
  }, [form, navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpsertReinsuredMutate = useCallback(
    debounce((reinsured: CreateReinsuredPayloadDto) => {
      if (mode === 'update' && onSubmit) {
        onSubmit(reinsured)
          .then(() => {
            message.success(
              t('forms.updateRecordSuccess', { type: t('reinsured.reinsured') })
            );
            navigate(ENavigationPath.REINSURED_RECORDS);
          })
          .catch((error) => {
            handleError({
              error,
              defaultMessage: t('forms.updateRecordFail', {
                type: t('reinsured.reinsured'),
              }),
            });
          });
      } else {
        createReinsuredMutation.mutate(reinsured);
      }
    }, 500),
    [createReinsuredMutation, mode, onSubmit, navigate, t]
  );

  const handleCancel = useCallback(() => {
    // Check if the form has been modified
    const currentValues = form.getFieldsValue();
    const hasChanges = !isEqual(currentValues, initialValues);
    if (hasChanges) {
      setShowConfirmation(true);
    } else {
      resetAndNavigate();
    }
  }, [form, resetAndNavigate]);

  return (
    <FormContainer
      title={
        mode === 'create'
          ? t('reinsured.setupNewReinsured')
          : t('reinsured.updateReinsured')
      }
    >
      <Form
        form={form}
        layout="vertical"
        className="space-y-8"
        onValuesChange={async () => {
          const values = form.getFieldsValue();
          const isValid = await validateForm<CreateReinsuredPayloadDto>(
            values,
            form,
            fields,
            getValidationSchema(t)
          );
          setIsFormValid(isValid);
        }}
        initialValues={initialValues}
        onFinish={async (reinsured: CreateReinsuredPayloadDto) => {
          const sanitizedReinsured = sanitizeFormValue(reinsured);
          debouncedUpsertReinsuredMutate(sanitizedReinsured);
        }}
      >
        <FormSection title={t('reinsured.setupCompanyParty')}>
          <FormLayoutGroup>
            <Form.Item name="status" label={t('general.status')}>
              <SortedSelect
                allowClear
                placeholder={t('general.selectField', {
                  field: t('general.status'),
                })}
                options={Object.values(CreateReinsuredPayloadDtoStatusEnum).map(
                  (status) => ({ label: status, value: status })
                )}
                optionRender={(option) => (
                  <StatusIndicator
                    status={option.value as CreateReinsuredPayloadDtoStatusEnum}
                  />
                )}
                labelRender={(props) => (
                  <StatusIndicator
                    // eslint-disable-next-line react/prop-types
                    status={props.value as CreateReinsuredPayloadDtoStatusEnum}
                  />
                )}
              ></SortedSelect>
            </Form.Item>
            <Form.Item
              name="reinsuredGroup"
              label={t('reinsured.reinsuredGroup')}
            >
              <SortedSelect
                allowClear
                placeholder={t('general.selectField', {
                  field: t('reinsured.reinsuredGroup'),
                })}
                options={Object.values(
                  CreateReinsuredPayloadDtoReinsuredGroupEnum
                ).map((type) => ({
                  label: type,
                  value: type,
                }))}
              ></SortedSelect>
            </Form.Item>
          </FormLayoutGroup>
          <FormLayoutGroup>
            <Form.Item
              name="legalEntityName"
              label={t('reinsured.legalEntityName')}
            >
              <Input
                size="large"
                placeholder={t('reinsured.enterLegalEntityName')}
              />
            </Form.Item>
            <Form.Item name="name" label={t('reinsured.reinsuredName')}>
              <Input
                size="large"
                placeholder={t('reinsured.enterReinsuredName')}
              />
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>
        <FormSection title={t('general.address')}>
          <AddressForm
            resetFormField={(fieldName: string) =>
              form.setFieldsValue({ [fieldName]: undefined })
            }
            initialData={initialData as AddressFormInitialData}
          />
        </FormSection>
        <FormLayoutGroup mdColumns={1}>
          <Form.Item name="comment" label={t('general.comments')}>
            <TextArea
              rows={4}
              placeholder={t('general.enter', {
                field: t('general.comments'),
              })}
            />
          </Form.Item>
        </FormLayoutGroup>

        <FormActionButtons
          isConfirmDisabled={
            !isFormValid || isLoading || createReinsuredMutation.isLoading
          }
          onCancel={handleCancel}
          confirmText={t('general.save')}
        />
      </Form>
      <ConfirmationModal
        type={ConfirmationModalType.DANGER}
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={resetAndNavigate}
        title={t('forms.cancelConfirmationTitle')}
        content={t('forms.cancelConfirmationContent')}
      />
    </FormContainer>
  );
};

export default ReinsuredForm;
